import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';

// import AntConfig from '../utils/AntConfig';
import AntCondition from '../utils/AntCondition';
import AntLocalization from '../utils/AntLocalization';
import AntPage from './AntPage';
import AntAccordion from './AntAccordion';
import AntSettingPanel from './AntSettingPanel';
import AntErrorPage from './AntErrorPage';

import { AntGlobal, AntSession } from '../utils/AntContext';

export default forwardRef(function (props, ref) {
    const session = AntSession();

    const canRender = useMemo(() => {
        let _canRender = AntCondition.isValid(null, {
            whenSession: {
                session_status: { type: '!=', value: 'Restricted' }
            }
        });

        return _canRender;
    }, [session]);

    const appGlobal = AntGlobal();

    const [shouldRender, setShouldRender] = useState(1);
    const forceRender = useCallback(() => {
        // Session/settings vừa đổi thì mọi form phải được phép báo lại trạng thái
        // hiển thị. Nếu giữ `false` cũ, group đã unmount sẽ không bao giờ tự tính lại.
        setGroupsVisibility({});
        setShouldRender((v) => v + 1);
    }, []);

    const [groupsVisibility, setGroupsVisibility] = useState({});
    const handleGroupHasVisibleItems = useCallback((groupKey, hasVisible, evaluatedSession) => {
        setGroupsVisibility((prev) => {
            if (prev[groupKey]?.hasVisible === hasVisible && prev[groupKey]?.session === evaluatedSession) return prev;
            return { ...prev, [groupKey]: { hasVisible, session: evaluatedSession } };
        });
    }, []);

    useEffect(() => {
        // refreshToken cập nhật AntSessionContext. Bỏ toàn bộ kết quả lọc cũ để
        // lần render kế tiếp đánh giá bằng snapshot session mới.
        setGroupsVisibility({});
    }, [session]);

    const onSettingUpdated = (e) => {
        if (props.onSettingUpdated) props.onSettingUpdated(e);
        forceRender();
    };

    let isRender = shouldRender + (props.shouldRender ? props.shouldRender : 1);

    return (
        <React.Fragment>
            {isRender && (
                <AntPage
                    className={'ant-setting-page'}
                    marginLeft={5}
                    marginRight={10}
                    marginBottom={400}
                    render={props.render}
                    hideBackgroundImage={true}
                >
                    {!canRender && (
                        <AntErrorPage
                            // id={`ant-setting-error-page-${props.API.entityName}`}
                            text={AntLocalization.translateText({
                                vi: 'Không có quyền truy cập !',
                                en: 'Access Denied',
                                fr: 'Accès refusé',
                                es: 'Acceso denegado',
                                id: 'Akses ditolak',
                                ja: 'アクセス拒否',
                                zh: '访问被拒绝',
                                ko: '접근 거부',
                                hi: 'पहुँच अस्वीकृत',
                                ar: 'تم رفض الوصول'
                            })}
                        />
                    )}

                    {canRender && (
                        <AntAccordion isH5={true} showIndex={true} autoExpandAll={true}>
                            {props.groups
                                .map((gr, originalIndex) => ({
                                    gr,
                                    groupKey: `${gr.name || gr.key || gr.form?.settingGroup || gr.API?.entityName || 'group'}-${originalIndex}`
                                }))
                                .filter(({ gr }) => AntCondition.isValid(null, gr.render))
                                .map(({ gr, groupKey }) => {
                                    // Không dùng kết quả visibility của snapshot session trước trong
                                    // lúc effect refreshToken của các form chưa chạy xong.
                                    if (groupsVisibility[groupKey]?.session === session && groupsVisibility[groupKey]?.hasVisible === false)
                                        return null;

                                    if (gr.API && !gr.API.entityDisplay) {
                                        if (appGlobal && appGlobal.appConfig && appGlobal.appConfig.Services) {
                                            let found = appGlobal.appConfig.Services.find((s) => s.name === gr.API.entityName);
                                            if (found) gr.API.init({ entityDisplay: found.display });
                                        }
                                    }

                                    return (
                                        <AntSettingPanel
                                            API={gr.API}
                                            key={groupKey}
                                            title={
                                                gr.title
                                                    ? gr.title
                                                    : gr.API && gr.API.entityDisplay
                                                    ? gr.API.entityDisplay
                                                    : gr.entityDisplay
                                                    ? gr.entityDisplay
                                                    : ''
                                            }
                                            autoRefreshToken={true}
                                            autoRefreshPage={gr.autoRefreshPage}
                                            showAsCardField={props.showFormAsCardField !== undefined ? Boolean(props.showFormAsCardField) : true}
                                            showDetail={gr.showDetail}
                                            showHelp={false}
                                            showTitle={false}
                                            import={gr.import}
                                            add={gr.add}
                                            edit={gr.edit}
                                            form={gr.form}
                                            refreshKey={props.refreshGroupsOnSave ? shouldRender : undefined}
                                            grid={gr.grid}
                                            tree={gr.tree}
                                            onSettingUpdated={onSettingUpdated}
                                            onHasVisibleItems={
                                                gr.form
                                                    ? (hasVisible) => handleGroupHasVisibleItems(groupKey, hasVisible, session)
                                                    : undefined
                                            }
                                        >
                                            {gr.children}
                                        </AntSettingPanel>
                                    );
                                })}
                        </AntAccordion>
                    )}
                    {canRender && props.children}
                </AntPage>
            )}
        </React.Fragment>
    );
});
